import React from "react";
import { Facebook, Instagram, Radio } from "lucide-react";
import { TikTokIcon } from "./icons/TikTokIcon";
import { socialBoost } from "../content/landingContent";
import { Reveal } from "./Reveal";
import { useCountUp } from "../hooks/useCountUp";
import { useContentField, useContentList } from "../lib/useContent";

const LiveCountdown: React.FC = () => {
  const { ref, value } = useCountUp({ from: 59, to: 0, duration: 2600 });
  const compareLive = useContentField("socialBoost.compareLive", socialBoost.compareLive);
  const minutes = Math.floor(value / 60);
  const seconds = value % 60;
  return (
    <div ref={ref} data-testid="live-countdown-card" className="rounded-2xl bg-white/15 border border-white/30 p-6 shadow-[0_15px_35px_-15px_rgba(0,0,0,0.4)]">
      <div className="flex items-center gap-2 text-white/90 text-xs font-display font-semibold uppercase tracking-widest">
        <Radio className="h-4 w-4 text-white/90" />
        {compareLive}
      </div>
      <div className="mt-4 font-display font-extrabold text-4xl text-white tabular-nums">
        {String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")}
      </div>
      <p className="mt-2 text-sm text-white/75">El contador llega a cero al finalizar el live.</p>
    </div>
  );
};

export const SocialBoost: React.FC = () => {
  const title = useContentField("socialBoost.title", socialBoost.title);
  const mainText = useContentField("socialBoost.mainText", socialBoost.mainText);
  const supportText = useContentField("socialBoost.supportText", socialBoost.supportText);
  const benefits = useContentList("socialBoost.benefitsList", socialBoost.benefits);
  const compareMotor = useContentField("socialBoost.compareMotor", socialBoost.compareMotor);
  return (
    <section
      id="potencia-redes"
      data-testid="social-boost-section"
      className="relative bg-motor-crimson py-20 sm:py-24 lg:py-28 overflow-hidden"
    >
      <div
        aria-hidden="true"
        className="absolute -bottom-24 -right-24 h-[380px] w-[380px] rounded-full bg-black/10 blur-3xl"
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <span className="font-display text-xs font-semibold uppercase tracking-[0.2em] text-white">
              Capítulo 03
            </span>
            <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl lg:text-[2.75rem] text-white leading-tight">
              {title}
            </h2>
            <p className="mt-5 font-display font-semibold text-lg text-white leading-relaxed">
              {mainText}
            </p>
            <p className="mt-4 text-base text-white leading-relaxed">{supportText}</p>

            <div className="mt-7 flex items-center gap-4">
              <Facebook className="h-6 w-6 text-white" />
              <Instagram className="h-6 w-6 text-white" />
              <TikTokIcon className="h-6 w-6 text-white" />
            </div>

            <ul className="mt-8 space-y-3">
              {benefits.map((b, idx) => (
                <li key={idx} className="flex items-start gap-3 text-sm sm:text-base text-white">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-white shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal variant="right" delay={0.1} className="grid gap-5">
            <LiveCountdown />
            <div
              data-testid="motorpro-always-on-card"
              className="rounded-2xl bg-white p-6 shadow-[0_20px_45px_-15px_rgba(0,0,0,0.35)]"
            >
              <div className="flex items-center gap-2 text-motor-crimson text-xs font-display font-semibold uppercase tracking-widest">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-motor-crimson/60 motion-reduce:animate-none" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-motor-crimson" />
                </span>
                {compareMotor}
              </div>
              <div className="mt-4 font-display font-extrabold text-4xl text-motor-ink">24/7</div>
              <p className="mt-2 text-sm text-neutral-500">
                Tu inventario sigue disponible, sin importar la hora.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
